/**
 * Canonical analytics vocabulary. Every event name and every dataLayer key used
 * by this module comes from here — there are no analytics string literals at
 * call sites or inside `events.ts`. Keeping the wire contract in one frozen
 * place is what lets the module stay in step across agapae.gr, app.agapae.gr,
 * and invite.agapae.gr without the copies drifting apart.
 *
 * These values ARE the GTM contract: GTM triggers/tags key off them, so they
 * must never change once published — see docs/analytics/events.md. This file is
 * the TypeScript rendition of the canonical landing-page/src/lib/analytics
 * module; the values here must match it (enforced by `npm run verify:analytics`).
 */

/**
 * dataLayer `event` names. Mirrors the Event Catalog in docs/analytics/events.md.
 */
export const AnalyticsEvents = Object.freeze({
  CTA_CLICK: 'cta_click',
  GENERATE_LEAD: 'generate_lead',
  SECTION_VIEW: 'section_view',
  CONTACT_VIEW: 'contact_view',
  CONTACT_FORM_SUBMIT: 'contact_form_submit',
  NEWSLETTER_SUBSCRIBED: 'newsletter_subscribed',
  // Authentication (app.agapae.gr). Browser-owned intent event; the backend
  // milestones (signup_completed / email_verified / login) are Backend-owned and
  // are NOT in this browser vocabulary — see docs/analytics/event-ownership.md.
  SIGNUP_STARTED: 'signup_started',
  // Payments (app.agapae.gr). Browser-owned begin-checkout INTENT, fired when the
  // couple starts a plan checkout before redirecting to Stripe. NOT a conversion —
  // the authoritative `purchase` is Backend-owned (Stripe webhook) and is NOT in
  // this browser vocabulary — see docs/analytics/event-ownership.md §8.
  CHECKOUT_STARTED: 'checkout_started',
  // Guest Engagement — Gallery (app.agapae.gr). Browser-owned upload-funnel and
  // gallery-engagement events. The authoritative storage truth (media_uploaded)
  // and download truth (download_zip) are Backend-owned and are NOT in this
  // browser vocabulary — see docs/analytics/event-ownership.md §6.
  //   upload_started  — once per upload batch, before bytes transfer.
  //   upload_failed   — per file, genuine failures only (not cancellation).
  //   gallery_opened  — once per page session, couple or guest gallery.
  UPLOAD_STARTED: 'upload_started',
  UPLOAD_FAILED: 'upload_failed',
  GALLERY_OPENED: 'gallery_opened',
  // Guest Engagement — Invitation (invite.agapae.gr). Browser-owned engagement,
  // fired once when the invitation SPA loads a public event. The authoritative
  // join (guest_joined) is Backend-owned — see event-ownership.md §5.
  INVITATION_OPENED: 'invitation_opened',
  // RSVP (invite.agapae.gr). Browser-owned INTENT, fired once per invitation
  // page session when the guest opens the RSVP flow (the modal). NOT the RSVP
  // truth: the authoritative rsvp_submitted / rsvp_requested transitions are
  // Backend-owned and are NOT in this browser vocabulary — see
  // docs/analytics/event-ownership.md §7.
  RSVP_STARTED: 'rsvp_started',
} as const);

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

/**
 * dataLayer keys: the reserved `event` key plus the parameters carried
 * alongside it. Parameter *values* (e.g. which CTA `location`, which `section`,
 * which `plan`) remain call-site vocabulary — only the keys are standardized
 * here.
 *
 * `PAGE` + `LANGUAGE` are the shared enrichment carried by every marketing
 * event — the CTA/conversion events (`cta_click`, `generate_lead`) and the
 * engagement/discovery events (`section_view`, `contact_view`,
 * `contact_form_submit`, `newsletter_subscribed`) — recording which page the
 * interaction happened on and the visitor's language. `CTA_NAME` + `PLAN`
 * enrich only the CTA/conversion events (the semantic name of the CTA and the
 * plan it points at, when applicable). `EVENT_TYPE` + `REF` enrich the
 * `signup_started` auth event (which celebration type, and the referring CTA).
 * `FROM_PLAN` + `SOURCE` enrich the `checkout_started` payment event (the tier
 * held before the upgrade, and where checkout began — `signup` | `billing`);
 * `PLAN` there carries the tier being bought.
 *
 * The Guest Engagement events (see docs/analytics/foundation.md) add:
 * `FILE_COUNT` / `PHOTO_COUNT` / `VIDEO_COUNT` (the size + composition of an
 * upload batch), `UPLOAD_SOURCE` (the upload-window phase), `ROLE`
 * (`guest` | `couple`), `MEDIA_TYPE` (`image` | `video`) and `REASON` (why an
 * upload failed) on `upload_started` / `upload_failed`; `ROLE` + `EVENT_ID`
 * (+ `PLAN`) on `gallery_opened`; and `EVENT_ID` + `EVENT_TYPE` + `LANGUAGE` on
 * `invitation_opened`. `ROLE` / `UPLOAD_SOURCE` / `MEDIA_TYPE` / `EVENT_ID`
 * deliberately match the Backend Gallery vocabulary so both halves of the
 * upload funnel report on the same dimensions. See docs/analytics/foundation.md
 * for the value vocabularies.
 *
 * The RSVP intent event (`rsvp_started`) adds no new keys — it reuses
 * `EVENT_ID` + `EVENT_TYPE` (the invitation whose RSVP flow opened and its
 * celebration type), so the guest RSVP funnel — `invitation_opened` →
 * `rsvp_started` → the Backend-owned `rsvp_submitted` — reports on the same
 * `event_id` / `event_type` dimensions end to end.
 */
export const AnalyticsParameters = Object.freeze({
  EVENT: 'event',
  LOCATION: 'location',
  SECTION: 'section',
  PAGE: 'page',
  LANGUAGE: 'language',
  CTA_NAME: 'cta_name',
  PLAN: 'plan',
  EVENT_TYPE: 'event_type',
  REF: 'ref',
  FROM_PLAN: 'from_plan',
  SOURCE: 'source',
  FILE_COUNT: 'file_count',
  PHOTO_COUNT: 'photo_count',
  VIDEO_COUNT: 'video_count',
  UPLOAD_SOURCE: 'upload_source',
  ROLE: 'role',
  MEDIA_TYPE: 'media_type',
  REASON: 'reason',
  EVENT_ID: 'event_id',
} as const);
